<template>
    <div class="checker-export-options">
        <h2 class="checker-export-options__heading">Export Options</h2>
        <p>(Please select which report/s you want to export and the format).</p>
        <br />

        <div class="checker-export-options__row">
            <label v-if="ukTrademarkSearch || euTrademarkSearch" class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportTrademarks" type="checkbox" label="TRADEMARKS"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <label v-if="socialSearch" class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportSocials" type="checkbox" label="SOCIALS"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <label v-if="domainSearch" class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportDomains" type="checkbox" label="DOMAINS"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <label v-if="webSearch" class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportWeb" type="checkbox" label="WEB SEARCHES"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>
        </div>

        <div class="checker-export-options__row">
            <label class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportPdf" type="checkbox" label="EXPORT AS PDF"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <label class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportXlsx" type="checkbox" label="EXPORT AS XLSX"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <label class="checker-export-options__checkbox-container">
                <FormulateInput v-model="exportCsv" type="checkbox" label="EXPORT AS CSV"
                    :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                    :wrapper-class="['formulate-input-wrapper--checkbox']"
                    :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                    :label-class="['checker-export-options__contained-checkbox-label']" />
            </label>

            <Transition name="fade">
                <label v-if="enableZipExport" class="checker-export-options__checkbox-container">
                    <FormulateInput v-model="exportZip" type="checkbox" label="EXPORT AS ZIP"
                        :outer-class="['checker-export-options__outer-checkbox-wrapper']"
                        :wrapper-class="['formulate-input-wrapper--checkbox']"
                        :element-class="['formulate-input-element--checkbox-small', 'checker-export-options__checkbox-wrapper']"
                        :label-class="['checker-export-options__contained-checkbox-label']" />
                </label>
            </Transition>
        </div>

        <div class="checker-export-options__row">
            <a :href="downloadUrl" download="test" target="_blank"
                :class="['checker-export-options__export-btn', { 'checker-export-options__export-btn--disabled': !enableDownload }]"
                @click="exportData($event)">
                <span v-if="!downloading">EXPORT FILES</span>
                <span v-else>EXPORTING FILES</span>
                <InlineSvg v-if="!downloading" :src="require(`../../assets/svgs/export.svg`)"
                    class="checker-export-options__export-icon" />
                <div v-else class="loader"></div>
            </a>
        </div>
    </div>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import userMixin from "@/mixins/userMixin";

export default {
    name: 'CheckerExportOptions',
    props: {
        searchToken: {
            type: Number,
            required: true,
        },
        ukTrademarkSearch: {
            type: Boolean,
            required: true,
        },
        euTrademarkSearch: {
            type: Boolean,
            required: true,
        },
        socialSearch: {
            type: Boolean,
            required: true,
        },
        socialMentionsSearch: {
            type: Boolean,
            required: true,
        },
        socialUsersSearch: {
            type: Boolean,
            required: true,
        },
        domainSearch: {
            type: Boolean,
            required: true,
        },
        webSearch: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            exportTrademarks: false,
            exportSocials: false,
            exportDomains: false,
            exportWeb: false,
            exportPdf: false,
            exportXlsx: false,
            exportCsv: false,
            exportZip: 0,
            downloading: false,
        }
    },
    watch: {
        exportPdf: function (currentValue) {
            if (currentValue) {
                this.exportXlsx = false;
                this.exportCsv = false;
            }
        },
        exportXlsx: function (currentValue) {
            if (currentValue) {
                this.exportPdf = false;
                this.exportCsv = false;
            }
        },
        exportCsv: function (currentValue) {
            if (currentValue) {
                this.exportPdf = false;
                this.exportXlsx = false;
                this.exportZip = false;
            }
        },
    },
    computed: {
        exportFileType() {
            return this.exportXlsx ? 'xlsx' : this.exportPdf ? 'pdf' : this.exportCsv ? 'csv' : '';
        },
        fileExtension() {
            if (this.exportZip) {
                return 'zip';
            } else {
                if (this.exportCsv) {
                    if (this.exportTrademarks) {
                        return 'zip';
                    } else {
                        let counter = 0;

                        if (this.exportSocials) counter++;
                        if (this.exportDomains) counter++;
                        if (this.exportWeb) counter++;

                        // if more than one data option is selected
                        if (counter > 1) {
                            return 'zip';
                        } else {
                            return 'csv';
                        }
                    }
                } else {
                    return this.exportXlsx ? 'xlsx' : this.exportPdf ? 'pdf' : '';
                }
            }
        },
        dataSources() {
            let data = [];

            if (this.exportTrademarks) {
                if (this.ukTrademarkSearch) {
                    data.push('&data_sources[]=uk_trademarks');
                }

                if (this.euTrademarkSearch) {
                    data.push('&data_sources[]=eu_trademarks');
                }
            }

            if (this.exportSocials) {
                if (this.socialMentionsSearch) {
                    data.push('&data_sources[]=social');
                }

                if (this.socialUsersSearch) {
                    data.push('&data_sources[]=social_users');
                }
            }

            if (this.exportDomains) {
                data.push('&data_sources[]=domains')
            }

            if (this.exportWeb) {
                data.push('&data_sources[]=web');
            }

            return data.join('');
        },
        downloadUrl() {
            return `${process.env.VUE_APP_API_ROOT_URL}/api/export/${this.searchToken}?format=${this.exportFileType}${this.dataSources}&zip=${this.exportZip ? 1 : 0}`;
        },
        enableDownload() {
            if ((this.exportTrademarks || this.exportSocials || this.exportDomains || this.exportWeb) && (this.exportPdf || this.exportXlsx || this.exportCsv)) {
                return true;
            }

            return false;
        },
        enableZipExport() {
            if (this.exportCsv) {
                return false;
            }

            return true;
        },
    },
    components: {
        InlineSvg
    },
    methods: {
        exportData(e) {
            e.preventDefault();

            this.downloading = true;

            let anchor = document.createElement("a");
            let fileExe = this.fileExtension;
            document.body.appendChild(anchor);

            var xhr = new XMLHttpRequest();
            xhr.open('GET', `${process.env.VUE_APP_API_ROOT_URL}/api/export/${this.searchToken}?format=${this.exportFileType}${this.dataSources}&zip=${this.exportZip ? 1 : 0}`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${userMixin.methods.getUserToken()}`);
            xhr.responseType = 'arraybuffer';
            xhr.onload = function () {
                if (this.status == 200) {
                    var blob = new Blob([this.response], { type: "application/pdf" });
                    var link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `export.${fileExe}`;
                    link.click();
                }
            };
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    this.downloading = false;
                }
            };
            xhr.send();
        },

        downloadBlob(blob, name) {
            // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
            const blobUrl = URL.createObjectURL(blob);

            // Create a link element
            const link = document.createElement("a");

            // Set link's href to point to the Blob URL
            link.href = blobUrl;
            link.download = name;

            // Append link to the body
            document.body.appendChild(link);

            // Dispatch click event on the link
            // This is necessary as link.click() does not work on the latest firefox
            link.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                })
            );

            // Remove link from body
            document.body.removeChild(link);
        }
    }
}
</script>